"use client";

import React, { useEffect, useMemo, useState } from "react";
import { Drawer, Fade } from "@mui/material";
import { useRouter } from "next/navigation";
import { useAtomValue } from "jotai";
import { Category, Subcategory } from "@webelocity/core";
import { categoriesAtom } from "@webelocity/state";
import { Button } from "@webelocity/ui";
import "./MobileShopDrawer.scss";

interface MobileShopDrawerProps {
  open: boolean;
  onClose: () => void;
}

const MobileShopDrawer: React.FC<MobileShopDrawerProps> = ({ open, onClose }) => {
  const router = useRouter();
  const categories = useAtomValue(categoriesAtom);
  const [activeCategoryId, setActiveCategoryId] = useState<string>("");
  const [isSubcategoryView, setIsSubcategoryView] = useState(false);

  const isLoading = categories.length === 0;

  const activeCategory = useMemo(
    () => categories.find((cat: Category) => cat._id === activeCategoryId),
    [categories, activeCategoryId]
  );

  useEffect(() => {
    if (!open) {
      setIsSubcategoryView(false);
      return;
    }

    if (!categories.length) {
      setActiveCategoryId("");
      return;
    }

    setActiveCategoryId((currentId) => {
      const exists = categories.some((category: Category) => category._id === currentId);
      return exists ? currentId : categories[0]._id;
    });
  }, [open, categories]);

  const handleSubcategoryClick = (subcategoryId: string) => {
    router.push(`/shop/catalogue?sub=${subcategoryId}&page=1`);
    onClose();
  };

  const handleCategoryClick = (categoryId: string) => {
    setActiveCategoryId(categoryId);
    setIsSubcategoryView(true);
  };

  const handleShopAllClick = () => {
    if (!activeCategoryId) {
      return;
    }

    router.push(`/shop/catalogue?cat=${activeCategoryId}&page=1`);
    onClose();
  };

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      PaperProps={{ className: "mobile-shop-drawer-paper" }}
    >
      <div className="mobile-shop-drawer-content">
        <div className="mobile-shop-drawer-header">
          {isSubcategoryView ? (
            <>
              <button
                type="button"
                className="mobile-shop-back-btn"
                onClick={() => setIsSubcategoryView(false)}
              >
                Back to categories
              </button>
              <p className="mobile-shop-title">{activeCategory?.name || "Category"}</p>
              <p className="mobile-shop-subtitle">Choose a subcategory to see products.</p>
            </>
          ) : (
            <>
              <p className="mobile-shop-title">Shop</p>
              <p className="mobile-shop-subtitle">Choose a category to continue.</p>
            </>
          )}
        </div>

        <Fade in={!isSubcategoryView} timeout={220} mountOnEnter unmountOnExit>
          <div className="mobile-shop-view mobile-shop-categories-list">
            {isLoading
              ? [...Array(6)].map((_, index) => (
                  <div key={index} className="mobile-shop-skeleton-category-list" />
                ))
              : categories.map((category: Category) => (
                  <button
                    key={category._id}
                    type="button"
                    className="mobile-shop-category-list-btn"
                    onClick={() => handleCategoryClick(category._id)}
                  >
                    {category.name}
                  </button>
                ))}
          </div>
        </Fade>

        <Fade in={isSubcategoryView} timeout={220} mountOnEnter unmountOnExit>
          <div className="mobile-shop-view mobile-shop-subcategory-layer">
            <div className="mobile-shop-subcategories">
              {isLoading ? (
                [...Array(8)].map((_, index) => (
                  <div key={index} className="mobile-shop-skeleton-subcategory" />
                ))
              ) : (
                <>
                  {(activeCategory?.categorySubCategories ?? []).map((subcategory: Subcategory) => (
                    <button
                      key={subcategory._id}
                      type="button"
                      className="mobile-shop-subcategory-btn"
                      onClick={() => handleSubcategoryClick(subcategory._id)}
                    >
                      {subcategory.name}
                    </button>
                  ))}
                  {activeCategory?.categorySubCategories?.length === 0 && (
                    <p className="mobile-shop-empty">No subcategories available yet.</p>
                  )}
                </>
              )}
            </div>

            <div className="mobile-shop-footer">
              <Button variant="outline" onClick={handleShopAllClick} disabled={!activeCategoryId}>
                Shop All in {activeCategory?.name || "Category"}
              </Button>
            </div>
          </div>
        </Fade>
      </div>
    </Drawer>
  );
};

export default MobileShopDrawer;
